import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Loader2 } from "lucide-react";
import { COMPANY, PRODUCTS } from "./data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTACT_CARDS = [
  { icon: Phone, label: "Call us", value: COMPANY.phone, href: `tel:${COMPANY.phoneRaw}`, testid: "contact-card-phone" },
  { icon: Mail, label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}`, testid: "contact-card-email" },
  { icon: MapPin, label: "Visit", value: COMPANY.address, testid: "contact-card-address" },
  { icon: Clock, label: "Hours", value: COMPANY.hours, testid: "contact-card-hours" },
];

export default function Contact({ preselectedProduct }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", company: "", product: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (preselectedProduct) setForm((f) => ({ ...f, product: preselectedProduct }));
  }, [preselectedProduct]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please provide your name and phone number.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        company: form.company.trim() || null,
        product: form.product || null,
        message: form.message.trim() || null,
      });
      toast.success("Enquiry received. Our team will call you back within one working day.");
      setForm({ name: "", phone: "", email: "", company: "", product: "", message: "" });
    } catch {
      toast.error("Could not send right now — please call or WhatsApp us instead.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border border-slate-300 rounded-md px-3 py-2.5 text-slate-900 focus:border-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-700 transition-colors bg-white";

  return (
    <section id="contact" data-testid="contact-section" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-700 mb-3">Get a quote / enquiry</p>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-8">Talk to our team</h2>
          <div className="space-y-4">
            {CONTACT_CARDS.map((c) => (
              <div key={c.label} className="flex items-start gap-4 bg-slate-50 border border-slate-200 rounded-xl p-5">
                <c.icon className="w-5 h-5 text-blue-700 mt-0.5 shrink-0" strokeWidth={1.75} />
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-0.5">{c.label}</p>
                  {c.href ? (
                    <a data-testid={c.testid} href={c.href} className="font-semibold text-slate-900 hover:text-blue-700 transition-colors break-all">
                      {c.value}
                    </a>
                  ) : (
                    <p data-testid={c.testid} className="font-semibold text-slate-900 leading-snug">{c.value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.form
          data-testid="quote-enquiry-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-slate-50 border border-slate-200 rounded-xl p-6 sm:p-8 self-start"
        >
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-700 mb-6">
            {form.product ? "Quote request" : "General enquiry"}
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-xs font-medium text-slate-500 mb-1.5">Name *</span>
              <input data-testid="form-input-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-slate-500 mb-1.5">Phone *</span>
              <input data-testid="form-input-phone" required value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+8801…" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-slate-500 mb-1.5">Email</span>
              <input data-testid="form-input-email" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-slate-500 mb-1.5">Company</span>
              <input data-testid="form-input-company" value={form.company} onChange={set("company")} className={inputCls} placeholder="Factory / company" />
            </label>
          </div>
          <label className="block mt-4">
            <span className="block text-xs font-medium text-slate-500 mb-1.5">Product of interest</span>
            <select data-testid="form-select-product" value={form.product} onChange={set("product")} className={inputCls}>
              <option value="">— General enquiry —</option>
              {PRODUCTS.map((p) => (
                <option key={p.id} value={p.name}>{p.name}</option>
              ))}
            </select>
          </label>
          <label className="block mt-4">
            <span className="block text-xs font-medium text-slate-500 mb-1.5">Message</span>
            <textarea data-testid="form-input-message" rows={4} value={form.message} onChange={set("message")}
              className={`${inputCls} resize-none`} placeholder="Belt dimensions, quantity, application…" />
          </label>
          <button
            data-testid="form-submit-button"
            type="submit"
            disabled={sending}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white font-semibold py-3 rounded-md transition-colors"
          >
            {sending && <Loader2 className="w-4 h-4 animate-spin" />}
            {sending ? "Sending…" : "Send Enquiry"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
